import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Pagination,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
} from "@mui/material";
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  orderBy,
  limit,
  where,
  startAfter,
  documentId,
} from "firebase/firestore";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PersonIcon from "@mui/icons-material/Person";
import { debounce } from "lodash";
import { AppContext } from "../../../store/AppContext";
import { db } from "../../../firebase";

const PAGE_SIZE = 10;

const Customers = () => {
  const { setCustomerTabInfo, customerTabInfo } = useContext(AppContext);
  const { setHeaderData, setLoader } = useContext(AppContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const [customers, setCustomers] = useState([]);
  const searchTerm = customerTabInfo?.searchTerm || "";
  const statusFilter = customerTabInfo?.statusFilter || "All";
  const page = customerTabInfo?.page || 1;

  /** Firestore cursors for browse pagination (index = page number). */
  const pageCursorsRef = useRef([null]);
  /** Light `users` docs — loaded once for search / status filter. */
  const usersIndexRef = useRef(null);
  /** Skip resetting page on first mount (keeps page after ViewCustomer back). */
  const filtersReadyRef = useRef(false);

  const filters = ["All", "Active", "Inactive"];

  const formatOrderDate = (recentOrder) => {
    if (!recentOrder) return null;
    const ts = recentOrder.timestamp || recentOrder.createdAt;
    if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleDateString();
    if (ts?.toDate) return ts.toDate().toLocaleDateString();
    if (ts) return new Date(ts).toLocaleDateString();
    return "N/A";
  };

  /** Enrich only the visible page (not every customer in the DB). */
  const enrichCustomers = async (list) =>
    Promise.all(
      list.map(async (customer) => {
        try {
          let defaultAddress = {};
          try {
            const defaultSnap = await getDocs(
              query(
                collection(db, "users", customer.id, "addresses"),
                where("is_default", "==", true),
                limit(1)
              )
            );
            if (!defaultSnap.empty) {
              defaultAddress = defaultSnap.docs[0].data();
            } else {
              const anySnap = await getDocs(
                query(
                  collection(db, "users", customer.id, "addresses"),
                  limit(1)
                )
              );
              defaultAddress = anySnap.docs[0]?.data() || {};
            }
          } catch {
            /* ignore */
          }

          let totalOrders = 0;
          try {
            const countSnap = await getCountFromServer(
              collection(db, "users", customer.id, "orders")
            );
            totalOrders = countSnap.data().count;
          } catch {
            totalOrders = 0;
          }

          let recentOrder = null;
          try {
            const recentSnap = await getDocs(
              query(
                collection(db, "users", customer.id, "orders"),
                orderBy("timestamp", "desc"),
                limit(1)
              )
            );
            recentOrder = recentSnap.docs[0]?.data() || null;
          } catch {
            recentOrder = null;
          }

          return {
            ...customer,
            firstName: defaultAddress.firstName || customer.firstName || "",
            lastName: defaultAddress.lastName || customer.lastName || "",
            email: defaultAddress.email || customer.email || "",
            phone: defaultAddress.mobileNumber || customer.phone || "",
            totalOrders,
            lastOrder: recentOrder
              ? {
                  date: formatOrderDate(recentOrder),
                  amount: recentOrder.items_total,
                  status: recentOrder.status,
                }
              : null,
          };
        } catch (error) {
          console.error("Error enriching customer:", customer.id, error);
          return customer;
        }
      })
    );

  const matchesSearch = (c, term) => {
    const haystack = [
      c.user_id,
      c.firstName,
      c.lastName,
      `${c.firstName || ""} ${c.lastName || ""}`,
      c.email,
      c.phone,
      c.id,
    ]
      .filter(Boolean)
      .map((v) => String(v).toLowerCase());
    return haystack.some((h) => h.includes(term));
  };

  const ensureUsersIndex = async () => {
    if (usersIndexRef.current) return usersIndexRef.current;
    const snapshot = await getDocs(collection(db, "users"));
    usersIndexRef.current = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    return usersIndexRef.current;
  };

  const loadCustomersPage = useCallback(async () => {
    setLoader(true);
    try {
      const term = searchTerm.toLowerCase().trim();
      const statusValue =
        statusFilter === "Active"
          ? "active"
          : statusFilter === "Inactive"
            ? "inactive"
            : null;

      // Search or status filter: light users index + enrich current page only.
      if (term || statusValue) {
        const index = await ensureUsersIndex();
        let matched = index;
        if (term) matched = matched.filter((c) => matchesSearch(c, term));
        if (statusValue) {
          matched = matched.filter((c) => c.account_status === statusValue);
        }
        const totalPages = Math.max(1, Math.ceil(matched.length / PAGE_SIZE));
        setCustomerTabInfo((prev) => ({ ...prev, totalPages }));
        const start = (page - 1) * PAGE_SIZE;
        setCustomers(
          await enrichCustomers(matched.slice(start, start + PAGE_SIZE))
        );
        return;
      }

      // Default browse: one Firestore page of users, then enrich those ~10 only.
      // If user jumps ahead in pagination, walk cursors forward first.
      let snapshot;
      if (page === 1 || pageCursorsRef.current[page - 1]) {
        const cursor = pageCursorsRef.current[page - 1];
        snapshot = await getDocs(
          cursor
            ? query(
                collection(db, "users"),
                orderBy(documentId()),
                startAfter(cursor),
                limit(PAGE_SIZE)
              )
            : query(
                collection(db, "users"),
                orderBy(documentId()),
                limit(PAGE_SIZE)
              )
        );
      } else {
        let walkPage = 1;
        let walkCursor = null;
        snapshot = await getDocs(
          query(
            collection(db, "users"),
            orderBy(documentId()),
            limit(PAGE_SIZE)
          )
        );
        if (snapshot.docs.length) {
          pageCursorsRef.current[1] = snapshot.docs[snapshot.docs.length - 1];
        }
        while (walkPage < page && snapshot.docs.length === PAGE_SIZE) {
          walkCursor = pageCursorsRef.current[walkPage];
          walkPage += 1;
          snapshot = await getDocs(
            query(
              collection(db, "users"),
              orderBy(documentId()),
              startAfter(walkCursor),
              limit(PAGE_SIZE)
            )
          );
          if (snapshot.docs.length) {
            pageCursorsRef.current[walkPage] =
              snapshot.docs[snapshot.docs.length - 1];
          }
        }
      }

      const pageDocs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (snapshot.docs.length > 0) {
        pageCursorsRef.current[page] =
          snapshot.docs[snapshot.docs.length - 1];
      }

      try {
        const countSnap = await getCountFromServer(collection(db, "users"));
        setCustomerTabInfo((prev) => ({
          ...prev,
          totalPages: Math.max(1, Math.ceil(countSnap.data().count / PAGE_SIZE)),
        }));
      } catch {
        setCustomerTabInfo((prev) => ({
          ...prev,
          totalPages: Math.max(
            prev?.totalPages || 1,
            snapshot.docs.length >= PAGE_SIZE ? page + 1 : page
          ),
        }));
      }

      setCustomers(await enrichCustomers(pageDocs));
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setLoader(false);
    }
  }, [searchTerm, statusFilter, page, setLoader, setCustomerTabInfo]);

  useEffect(() => {
    setHeaderData((prevState) => ({
      ...prevState,
      leftText: "Customers",
      back: true,
    }));
  }, [setHeaderData]);

  // Only reset to page 1 when the user changes search/filter — not on remount.
  useEffect(() => {
    if (!filtersReadyRef.current) {
      filtersReadyRef.current = true;
      return;
    }
    pageCursorsRef.current = [null];
    setCustomerTabInfo((prev) =>
      prev.page === 1 ? prev : { ...prev, page: 1 }
    );
  }, [searchTerm, statusFilter, setCustomerTabInfo]);

  useEffect(() => {
    loadCustomersPage();
  }, [loadCustomersPage]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setCustomerTabInfo((prev) => ({
        ...prev,
        searchTerm: value,
      }));
    }, 400),
    [setCustomerTabInfo]
  );

  const paginatedCustomers = customers;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleCustomerClick = (customer) => {
    setHeaderData((prevState) => {
      return {
        ...prevState,
        leftText: "Customer Details",
        back: false,
        leftAction: () => {
          // Keep page / search / statusFilter — only leave the detail view.
          setCustomerTabInfo((prevState) => ({
            ...prevState,
            viewCustomer: false,
            selectedCustomer: null,
          }));
          setHeaderData({
            leftText: "Customers",
            back: true,
          });
        },
      };
    });

    setCustomerTabInfo((prevState) => {
      return {
        ...prevState,
        viewCustomer: true,
        selectedCustomer: customer,
      };
    });
  };

  // Helper function to get status color
  function getStatusColor(status) {
    switch (status) {
      case "pending":
        return "#ff9800";
      case "shipped":
        return "#2196f3";
      case "delivered":
        return "#4caf50";
      case "cancelled":
        return "#f44336";
      default:
        return "#666";
    }
  }

  // Mobile Customer Card Component
  const CustomerCard = ({ customer }) => (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: 1,
        cursor: "pointer",
        "&:hover": {
          boxShadow: 3,
        },
      }}
      onClick={() => handleCustomerClick(customer)}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Avatar sx={{ bgcolor: "#023DE1", width: 48, height: 48 }}>
            <PersonIcon />
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#023DE1",
                  fontSize: "1rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {customer.firstName} {customer.lastName}
              </Typography>
              <Chip
                label={customer.account_status}
                size="small"
                sx={{
                  backgroundColor:
                    customer.account_status === "active"
                      ? "#e8f5e9"
                      : "#ffebee",
                  color:
                    customer.account_status === "active"
                      ? "#2e7d32"
                      : "#d32f2f",
                  fontWeight: 500,
                  ml: 1,
                }}
              />
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: "0.875rem" }}
            >
              #{customer.user_id}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {customer.email}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {customer.phone}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Orders
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {customer.totalOrders || 0}
                </Typography>
              </Box>

              {customer.lastOrder && (
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" color="text.secondary">
                    Last Order
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(customer.lastOrder.amount)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {customer.lastOrder.date}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "95%",
        margin: "auto",
        padding: isMobile ? "0 8px" : "0",
      }}
    >
      {/* Search Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "16px" : "0",
        }}
      >
        <div style={{ width: isMobile ? "100%" : "auto" }}>
          <FormControl
            size={isMobile ? "medium" : "small"}
            variant="outlined"
            fullWidth={isMobile}
          >
            <InputLabel>Search by ID, Name, Phone, Email</InputLabel>
            <OutlinedInput
              size={isMobile ? "medium" : "small"}
              placeholder="Search by ID, name, phone, email"
              defaultValue={searchTerm}
              onChange={(e) => debouncedSearch(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchOutlinedIcon />
                  </IconButton>
                </InputAdornment>
              }
              label="Search by ID, Name, Phone, Email"
            />
          </FormControl>
        </div>
      </div>

      {/* Filter Buttons */}
      <div
        style={{
          display: "flex",
          gap: isMobile ? "8px" : "10px",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: isMobile ? "center" : "flex-start",
        }}
      >
        {filters.map((data, index) => (
          <div
            key={index}
            className={`${
              statusFilter.toLocaleLowerCase() === data?.toLocaleLowerCase()
                ? "filter-button filter-active "
                : "filter-button"
            }`}
            onClick={() =>
              setCustomerTabInfo((prev) => ({
                ...prev,
                statusFilter: data,
              }))
            }
            style={{
              padding: isMobile ? "8px 16px" : "10px 20px",
              borderRadius: "20px",
              cursor: "pointer",
              backgroundColor:
                statusFilter.toLocaleLowerCase() === data?.toLocaleLowerCase()
                  ? "#023DE1"
                  : "#f5f5f5",
            }}
          >
            <h5
              style={{
                fontWeight: "400",
                margin: 0,
                fontSize: isMobile ? "14px" : "16px",
                color: `${
                  statusFilter.toLocaleLowerCase() === data?.toLocaleLowerCase()
                    ? "white"
                    : "#9f9e9f"
                }`,
              }}
            >
              {data}
            </h5>
          </div>
        ))}
      </div>

      {/* Mobile Card View */}
      {isMobile ? (
        <div style={{ width: "100%" }}>
          {paginatedCustomers.map((customer) => (
            <CustomerCard key={customer.id} customer={customer} />
          ))}
          {(customerTabInfo?.totalPages || 1) > 1 && (
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                marginTop: "16px",
              }}
            >
              <Pagination
                count={customerTabInfo?.totalPages || 1}
                page={page}
                onChange={(e, value) =>
                  setCustomerTabInfo((prevState) => ({
                    ...prevState,
                    page: value,
                  }))
                }
                color="primary"
                size="medium"
              />
            </div>
          )}
        </div>
      ) : (
        /* Desktop/Tablet Table View */
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            background: "white",
            padding: isTablet ? "8px" : "10px",
            borderRadius: "10px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            height: "max-content",
            overflowY: "scroll",
          }}
        >
          <div style={{ borderRadius: "8px", overflow: "hidden" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: isTablet ? "13px" : "14px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#f5f5f5",
                    height: "50px",
                    borderRadius: "8px",
                  }}
                >
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                      padding: isTablet ? "8px" : "12px",
                    }}
                  >
                    Customer ID
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                      padding: isTablet ? "8px" : "12px",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                      padding: isTablet ? "8px" : "12px",
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                      padding: isTablet ? "8px" : "12px",
                    }}
                  >
                    Phone
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                      padding: isTablet ? "8px" : "12px",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                      padding: isTablet ? "8px" : "12px",
                    }}
                  >
                    Last Order
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                      padding: isTablet ? "8px" : "12px",
                    }}
                  >
                    Total Orders
                  </th>
                </tr>
              </thead>
              <tbody className="scrollable-tbody">
                {paginatedCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    style={{
                      borderBottom: "1px solid #e0e0e0",
                      height: isTablet ? "50px" : "60px",
                    }}
                  >
                    <td
                      style={{
                        padding: isTablet ? "8px" : "12px",
                        color: "#023DE1",
                        fontSize: isTablet ? "13px" : "14px",
                        fontWeight: "400",
                        cursor: "pointer",
                      }}
                      onClick={() => handleCustomerClick(customer)}
                    >
                      #{customer.user_id}
                    </td>
                    <td
                      style={{
                        padding: isTablet ? "8px" : "12px",
                        fontSize: isTablet ? "13px" : "14px",
                        fontWeight: "400",
                      }}
                    >
                      {customer.firstName} {customer.lastName}
                    </td>
                    <td
                      style={{
                        padding: isTablet ? "8px" : "12px",
                        fontSize: isTablet ? "13px" : "14px",
                        fontWeight: "400",
                        maxWidth: isTablet ? "150px" : "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {customer.email}
                    </td>
                    <td
                      style={{
                        padding: isTablet ? "8px" : "12px",
                        fontSize: isTablet ? "13px" : "14px",
                        fontWeight: "400",
                      }}
                    >
                      {customer.phone}
                    </td>
                    <td
                      style={{
                        padding: isTablet ? "8px" : "12px",
                        fontSize: isTablet ? "13px" : "14px",
                        fontWeight: "400",
                      }}
                    >
                      <span
                        style={{
                          padding: isTablet ? "4px 8px" : "6px 12px",
                          borderRadius: "12px",
                          fontSize: isTablet ? "12px" : "14px",
                          backgroundColor:
                            customer.account_status === "active"
                              ? "#e8f5e9"
                              : "#ffebee",
                          color:
                            customer.account_status === "active"
                              ? "#2e7d32"
                              : "#d32f2f",
                        }}
                      >
                        ● {customer.account_status}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: isTablet ? "8px" : "12px",
                        fontSize: isTablet ? "12px" : "14px",
                        fontWeight: "400",
                      }}
                    >
                      {customer.lastOrder ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <span>
                            {formatCurrency(customer.lastOrder.amount)}
                          </span>
                          <span
                            style={{
                              fontSize: isTablet ? "10px" : "12px",
                              color: "#666",
                            }}
                          >
                            {customer.lastOrder.date}
                          </span>
                          <span
                            style={{
                              fontSize: isTablet ? "10px" : "12px",
                              color: getStatusColor(customer.lastOrder.status),
                            }}
                          >
                            {customer.lastOrder.status}
                          </span>
                        </div>
                      ) : (
                        "No orders"
                      )}
                    </td>
                    <td
                      style={{
                        padding: isTablet ? "8px" : "12px",
                        fontWeight: "400",
                        fontSize: isTablet ? "13px" : "14px",
                      }}
                    >
                      {customer.totalOrders || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: isMobile ? "center" : "end",
              marginTop: "20px",
            }}
          >
            <Pagination
              count={customerTabInfo?.totalPages || 1}
              page={page}
              onChange={(e, value) =>
                setCustomerTabInfo((prevState) => ({
                  ...prevState,
                  page: value,
                }))
              }
              color="primary"
              size="large"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
